import React, { useState } from "react";
import { TrendingUp, ChevronDown, ChevronUp, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";
import { negCard, fmtTermValue } from "./negotiationShared";

export function NegotiationAnalytics({ neg }) {
  const { tf, locale } = useLang();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    try {
      const r = await api.get(`/negotiations/${neg.id}/analytics`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setLoading(false); }
  };

  const fmt = (row, v) => fmtTermValue(row.vtype, row.currency, v, locale, tf);

  return (
    <div className={cn(negCard, "p-4")} data-testid="neg-analytics">
      <button type="button" onClick={toggle} className="w-full flex items-center gap-2 text-left" data-testid="neg-analytics-toggle">
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 flex-1">{tf("Analytique de négociation")}</h3>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
      ) : data && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-1.5" data-testid="neg-analytics-overview">
            <span className="text-[11px] px-2 py-0.5 rounded-full border border-border dark:border-border text-muted-foreground">{data.versions_count} {tf("versions finalisées")}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/25 text-emerald-500 bg-emerald-500/10">{data.counts.improved} {tf("en hausse")}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full border border-red-500/25 text-red-500 bg-red-500/10">{data.counts.degraded} {tf("en baisse")}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full border border-border dark:border-border text-muted-foreground">{data.counts.unchanged} {tf("inchangés")}</span>
          </div>

          {data.terms.length === 0 ? (
            <p className="text-sm text-muted-foreground">{tf("Finalisez au moins une version pour voir l'évolution.")}</p>
          ) : (
            <div className="space-y-2">
              {data.terms.map((row) => (
                <div key={row.term_key} className="rounded-xl border border-border/60 dark:border-border p-3" data-testid={`analytics-term-${row.term_key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{tf(row.label)}</span>
                    <div className="flex-1" />
                    {row.delta !== null && row.delta !== undefined && (
                      <span className={cn("text-xs font-semibold", row.delta > 0 ? "text-emerald-500" : row.delta < 0 ? "text-red-500" : "text-muted-foreground")} data-testid={`analytics-delta-${row.term_key}`}>
                        {row.delta > 0 ? "+" : ""}{fmt(row, row.delta)}{row.pct !== null && row.pct !== undefined ? ` (${row.pct > 0 ? "+" : ""}${row.pct} %)` : ""}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground" data-testid={`analytics-series-${row.term_key}`}>
                    {row.progression ? (
                      row.progression.map((p, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <ArrowRight className="h-3 w-3 shrink-0" />}
                          <span className="font-medium text-foreground">{fmt(row, p)}</span>
                        </React.Fragment>
                      ))
                    ) : (
                      row.series.map((s, i) => (
                        <React.Fragment key={s.version_number}>
                          {i > 0 && <ArrowRight className="h-3 w-3 shrink-0" />}
                          <span>V{s.version_number} <span className="font-medium text-foreground">{fmt(row, s.value)}</span></span>
                        </React.Fragment>
                      ))
                    )}
                  </div>
                  {(row.target !== null && row.target !== undefined) && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {tf("Objectif")} : <span className="font-medium text-foreground">{fmt(row, row.target)}</span>
                      {row.gap_remaining !== null && row.gap_remaining !== undefined && (
                        <> · {tf("Écart restant")} : <span className={cn("font-medium", row.gap_remaining <= 0 ? "text-emerald-500" : "text-amber-500")} data-testid={`analytics-gap-${row.term_key}`}>{fmt(row, row.gap_remaining)}</span></>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {data.athlete && data.athlete.negotiations_count > 1 && (
            <p className="text-[11px] text-muted-foreground" data-testid="analytics-athlete">
              {tf("Athlète")} : {data.athlete.negotiations_count} {tf("négociations")} · {tf("évolution salariale cumulée")}{" "}
              <span className={cn("font-medium", data.athlete.total_salary_delta >= 0 ? "text-emerald-500" : "text-red-500")}>
                {data.athlete.total_salary_delta > 0 ? "+" : ""}{fmtTermValue("money", "EUR", data.athlete.total_salary_delta, locale, tf)}
              </span>
            </p>
          )}
          <p className="text-[10px] text-muted-foreground">{tf("Calculs déterministes basés sur les versions finalisées — aucune note ni score arbitraire.")}</p>
        </div>
      ))}
    </div>
  );
}
